import { Box, useLayoutContext, useTheme } from "arkh";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { ActivityIndicator, FlatList } from "react-native";
import { FileListItem } from "./FileListItem";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { openFileType } from "./useOpenFile";

import { FileSearchNoFilesFound } from "./FileSearchNoFilesFound";
import {
  PreloadedQuery,
  fetchQuery,
  graphql,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";
import { SearchListQuery } from "./__generated__/SearchListQuery.graphql";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

const SEARCH_LIST_QUERY = graphql`
  query SearchListQuery($query: String!) {
    store {
      id
      fileSearch(query: $query) {
        id
        hits {
          id
          textHighlights {
            isHighlighted
            text
          }
          nameHighlights {
            isHighlighted
            text
          }
          file {
            id

            ...FileListItem_file
          }
        }
      }
    }
  }
`;

const SearchList_ = (props: { query: string; openFile: openFileType }) => {
  const environment = useRelayEnvironment();
  const [queryRef, loadQuery] =
    useQueryLoader<SearchListQuery>(SEARCH_LIST_QUERY);
  const [isSearching, setIsSearching] = useState(false);

  const search = useCallback(
    (query: string) => {
      // fetchQuery will fetch the query and write
      // the data to the Relay store. This will ensure
      // that when we re-render, the data is already
      // cached and we don't suspend
      fetchQuery(environment, SEARCH_LIST_QUERY, { query }).subscribe({
        complete: () => {
          setIsSearching(false);

          // *After* the query has been fetched, we call
          // loadQuery again to re-render with a new
          // queryRef.
          // At this point the data for the query should
          // be cached, so we use the 'store-only'
          // fetchPolicy to avoid suspending.
          loadQuery({ query }, { fetchPolicy: "store-only" });
        },
        error: () => {
          setIsSearching(false);
        },
      });
    },
    [environment, loadQuery]
  );

  useEffect(() => {
    search(props.query);
  }, [props.query, search]);

  const theme = useTheme();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        isSearching ? <ActivityIndicator color={theme.primary} /> : null,
    });
    return () => {
      navigation.setOptions({
        headerRight: () => null,
      });
    };
  }, [isSearching, navigation, theme.background, theme.primary]);

  return queryRef !== null && queryRef !== undefined ? (
    <Comp
      isSearching={isSearching}
      queryRef={queryRef}
      openFile={props.openFile}
    />
  ) : null;
};
export const SearchList = React.memo(SearchList_);
function Comp(props: {
  isSearching: boolean;
  queryRef: PreloadedQuery<SearchListQuery>;
  openFile: openFileType;
}) {
  const data = usePreloadedQuery(SEARCH_LIST_QUERY, props.queryRef);
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const searchResult = useMemo(() => {
    return data?.store.fileSearch.hits ?? [];
  }, [data?.store.fileSearch.hits]);

  const renderItem = useCallback(
    (file: { item: (typeof searchResult)[number]; index: number }) => {
      return (
        <Box spaceHorizontal="medium" spaceTop="medium">
          <FileListItem
            isFirst={file.index === 0}
            isLast={file.index === searchResult.length - 1}
            file={file.item.file}
            nameHighlights={file.item.nameHighlights}
            textHighlights={file.item.textHighlights}
            onPress={(_file) => {
              props.openFile({
                url: _file.url,
                fileId: _file.fileId,
                fileName: _file.fileName,
                fileType: _file.fileType,
                analytics: {
                  position: file.index,
                  queryId: data?.store.fileSearch.id ?? undefined,
                },
              });
            }}
          />
        </Box>
      );
    },
    [data?.store.fileSearch.id, props, searchResult.length]
  );

  const paddingBottom = insets.bottom + layout.space.large;

  if (searchResult.length === 0 && !props.isSearching) {
    return <FileSearchNoFilesFound />;
  }

  return (
    <FlatList
      contentContainerStyle={{
        paddingTop: layout.space.small,
        paddingBottom,
      }}
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="never"
      scrollIndicatorInsets={{
        bottom: paddingBottom,
      }}
      renderScrollComponent={(props) => <KeyboardAwareScrollView {...props} />}
      data={searchResult}
      renderItem={renderItem}
    />
  );
}
